import React, { useEffect } from "react";
import { Button, Loader, Text } from "@vibe/core";
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalBasicLayout } from "@vibe/core/next";
import { BulkSummary } from "../../hooks/api/subitemBulk.types";
import useBulkDrift from "../../hooks/api/useBulkDrift";
import { describeGroup, groupDriftRows, DriftGroup } from "./bulkDriftGrouping";
import { boardLabel, boardsPhrase, joinNames, plural } from "./resyncCopy";

/**
 * Estimated duration, from the pacing policy settled 2026-08-19 (pace to the
 * reserve). Presentation only — the job paces off Monday's reported complexity,
 * never off this. Kept in the component tree deliberately: a number the user is
 * shown must not be mistaken for the number the scheduler obeys.
 *
 * The constants below are compiled into this ONE presentational helper only.
 * The plan's Global Constraints forbid baking a complexity ceiling in anywhere
 * else, because the ceiling is plan-dependent (20,000,000/60s was measured on
 * one account) — but the stored BulkSummary carries no observed complexity
 * reading for the frontend to derive a number from instead. So this treats the
 * highest ceiling on record as a FLOOR: a lower real ceiling buys an account
 * fewer mutations per 60s window, which makes the job slower, never faster, so
 * a duration computed from the highest ceiling we've measured under-estimates
 * (or exactly matches) every account at or below it. Overstating the clobber
 * window is the safe direction for consent; understating it is not — which is
 * why the copy below says "at least about", not "about".
 */
export const estimateSeconds = (mutationCount: number): number => {
  const CEILING = 20_000_000,
    RESERVE = 1_000_000,
    PER_MUTATION = 30_000;
  const perWindow = Math.floor((CEILING - RESERVE) / PER_MUTATION); // 633
  return Math.round((mutationCount / perWindow) * 60);
};

// Minutes for display, rounded — never up. estimateSeconds is already phrased as
// a floor ("at least about"); ceiling-rounding the minutes on top of that would
// let the displayed number exceed the floor it's supposed to represent.
const estimateMinutes = (seconds: number): number => Math.max(1, Math.round(seconds / 60));

interface BulkConfirmModalProps {
  summary: BulkSummary;
  /**
   * The job whose plan the dialog lists. Optional so the dialog still renders (counts, boards,
   * ETA, consent) for a caller that has no job to page — the list is then simply absent.
   */
  jobId?: string | null;
  token?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  confirming?: boolean;
}

const GROUP_STYLE: React.CSSProperties = {
  border: "1px solid var(--layout-border-color)",
  borderRadius: "var(--border-radius-medium)",
  overflow: "hidden",
};

const GROUP_HEAD_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "var(--spacing-small)",
  padding: "var(--spacing-small) var(--spacing-medium)",
  background: "var(--primary-background-hover-color)",
  cursor: "pointer",
  listStyle: "none",
};

const ENTITY_ROW_STYLE: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(120px, 160px) 1fr",
  gap: "var(--spacing-small) var(--spacing-medium)",
  padding: "var(--spacing-small) var(--spacing-medium)",
  borderTop: "1px solid var(--layout-border-color)",
  alignItems: "start",
};

const COLUMN_CHIP_STYLE: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  height: 22,
  padding: "0 var(--spacing-small)",
  borderRadius: 11,
  fontSize: 12,
  fontWeight: 500,
  whiteSpace: "nowrap",
  background: "var(--primary-selected-color)",
  color: "var(--primary-text-color)",
};

const NOTICE_STYLE: React.CSSProperties = {
  padding: "var(--spacing-small) var(--spacing-medium)",
  borderRadius: "var(--border-radius-small)",
  background: "var(--warning-color-selected)",
};

/**
 * One item and everything under it that will change. Native `<details>`, open for the first
 * few groups so the dialog shows real content at rest, collapsed after that so forty-nine
 * entries fit in a medium modal without a wall.
 */
const DriftGroupView = ({
  group,
  boardNames,
  defaultOpen,
}: {
  group: DriftGroup;
  boardNames: Record<string, string>;
  defaultOpen: boolean;
}) => (
  <details style={GROUP_STYLE} open={defaultOpen}>
    <summary style={GROUP_HEAD_STYLE}>
      <Text type="text2" weight="medium" element="span" style={{ flex: 1 }}>
        {group.parentName}
      </Text>
      <Text type="text3" color="secondary" element="span">
        {describeGroup(group)}
      </Text>
    </summary>
    {group.entities.map((entity) => (
      <div key={`${entity.kind}-${entity.entityId}`} style={ENTITY_ROW_STYLE}>
        <div>
          <Text type="text2" weight="medium" element="div">
            {entity.name}
          </Text>
          <Text type="text3" color="secondary" element="div">
            {entity.kind === "parentItem" ? "item" : "subitem"}
          </Text>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-small)" }}>
          {entity.routes.map((route) => (
            <div
              key={`${route.sourceBoardId}->${route.targetBoardIds.join(",")}`}
              style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-xs)" }}
            >
              <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--spacing-xs)" }}>
                {route.columns.map((column) => (
                  <span key={column.columnKey} style={COLUMN_CHIP_STYLE}>
                    {column.columnTitle}
                  </span>
                ))}
              </div>
              <Text type="text3" color="secondary" element="div">
                {`From ${boardLabel(boardNames, route.sourceBoardId)} → ${joinNames(
                  route.targetBoardIds.map((boardId) => boardLabel(boardNames, boardId)),
                )}`}
              </Text>
            </div>
          ))}
        </div>
      </div>
    ))}
  </details>
);

/** Groups expanded on open. Enough to show the shape of the list; the rest are one click away. */
const GROUPS_OPEN_BY_DEFAULT = 3;

/**
 * The consent dialog for a board-wide bulk resync — the single surface where a user learns WHAT
 * is about to change on their boards and that it cannot be undone. Purely presentational apart
 * from paging the plan's drift rows; it holds no job state of its own.
 *
 * WHAT IT LISTS, AND WHAT IT DOES NOT. Every item, subitem and column the plan will write, and
 * for each the board the value comes from and the boards it goes to — all by name, from the
 * drift route. NOT the values themselves: the plan's values live only in a Redis snapshot under
 * a TTL (Spec Decision 3) and the projection has no field for them. Agreed with Mike
 * (2026-09-04): a bulk run touches many items, so the summary is what changes and where; the
 * before/after of any one cell is in the grid.
 *
 * No per-board value count, still: an earlier version apportioned `columnWrites` across boards
 * by a largest-remainder split, and it was withdrawn (2026-08-19) as a computed guess dressed up
 * as a fact on the one surface whose job is the truth about an irreversible write.
 */
const BulkConfirmModal: React.FC<BulkConfirmModalProps> = ({
  summary,
  jobId = null,
  token = null,
  onConfirm,
  onCancel,
  confirming,
}) => {
  const drift = useBulkDrift(token, jobId);
  // The hook fetches nothing until asked (its other consumer was a collapsed list). This dialog
  // IS the review step, so the first page is asked for as soon as it opens.
  useEffect(() => {
    if (jobId && token && drift.isUnloaded) void drift.loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, token, drift.isUnloaded]);
  const groups = groupDriftRows(drift.rows);
  // The summary's own names first (resolved for every affected board when the scan landed),
  // then the drift page's, which may know a board the summary's cache lookup missed.
  const boardNames = { ...(summary.boardNames ?? {}), ...drift.boardNames };

  const seconds = estimateSeconds(summary.mutationCount);
  const minutes = estimateMinutes(seconds);

  /*
   * What is affected, split by entity and never summed. One run writes a parent item's own
   * drifting columns alongside its subitems', on different boards, and consenting to writes on
   * parent boards is a materially different act from consenting on subitem boards. Absent
   * `parentsAffected` drops the clause rather than defaulting it: the field is missing when the
   * scan had no notion of parent-item drift, and "0 items" would be a claim about something
   * never looked at.
   */
  const affected =
    summary.parentsAffected === undefined
      ? plural(summary.subitemsAffected, "subitem")
      : `${plural(summary.parentsAffected, "item")} and ${plural(summary.subitemsAffected, "subitem")}`;

  const values = plural(summary.columnWrites, "value");
  const boards = boardsPhrase(summary.boardsAffected, Object.keys(boardNames).length > 0 ? boardNames : undefined);

  return (
    <Modal id="bulk-confirm" show onClose={onCancel} size="medium">
      <ModalBasicLayout>
        <ModalHeader title="Review and fix" className="px-8" />
        <ModalContent>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-medium)" }}>
            <Text type="text1" element="p">
              {`SIMB copies each value from the board where it was changed most recently to the other boards. ${values} will be updated on ${boards} (${affected} affected).`}
            </Text>

            {jobId ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-small)" }}>
                {groups.map((group, index) => (
                  <DriftGroupView
                    key={group.parentSyncId}
                    group={group}
                    boardNames={boardNames}
                    defaultOpen={index < GROUPS_OPEN_BY_DEFAULT}
                  />
                ))}

                {drift.isLoading ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-small)" }}>
                    <Loader size="small" color="primary" />
                    <Text type="text2" color="secondary">
                      Loading the list of changes…
                    </Text>
                  </div>
                ) : null}

                {/* The rows already fetched stay; the list is a courtesy, the counts above are the
                    consent. A user can still continue on the counts alone, as they always could. */}
                {drift.error ? (
                  <Text type="text2" color="negative" element="p">
                    {`${drift.error} You can still continue using the counts above.`}
                  </Text>
                ) : null}

                {!drift.isLoading && !drift.error && drift.total !== null && drift.total > 0 ? (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Text type="text3" color="secondary">
                      {`Showing ${drift.rows.length} of ${plural(drift.total, "change")}`}
                    </Text>
                    {drift.hasMore ? (
                      <Button size="small" kind="tertiary" onClick={() => void drift.loadMore()}>
                        Show more
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}

            <div style={NOTICE_STYLE}>
              <Text type="text2" element="p">
                {`This is an estimate: expect it to take at least about ${plural(minutes, "minute")}. Any edits made on these boards while this runs — including through those ${plural(minutes, "minute")} — will be overwritten, with no undo.`}
              </Text>
            </div>
          </div>
        </ModalContent>
      </ModalBasicLayout>
      <ModalFooter
        primaryButton={{
          onClick: onConfirm,
          text: `Update ${values}`,
          loading: confirming,
          color: "negative",
        }}
        secondaryButton={{ onClick: onCancel, text: "Cancel", disabled: confirming }}
      />
    </Modal>
  );
};

export default BulkConfirmModal;
